'use client'

import { Link, usePathname, useRouter } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { useProgress } from '@bprogress/next'
import { useLocale } from 'next-intl'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useTransition } from 'react'
import { Select } from '../../ui/select'
import { setUserLocale } from './locale'

type Language = {
  code: string
  label: string
}

type LanguageSwitcherItemProps = {
  label: string
  value: string
  className?: string
  isLoading?: boolean
}

const LANGUAGES: Language[] = [
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'en', label: 'English' },
]

const LanguageSwitcherItem = ({
  label,
  value,
  className,
  isLoading = false,
}: LanguageSwitcherItemProps) => (
  <span className={cn('flex items-center gap-2 font-medium', className)}>
    <Image
      src={value === 'en' ? '/icons/flag-us.png' : '/icons/flag-vn.png'}
      alt={value}
      width={16}
      height={16}
      className='h-4 w-4 rounded-full object-cover'
    />
    {label}
    {isLoading && (
      <div className='h-2.5 w-2.5 animate-spin rounded-full border border-neutral-300 border-t-neutral-600' />
    )}
  </span>
)

export default function LanguageSwitcher({
  gaTrackingValue,
  gaTrackingLabel,
}: {
  gaTrackingValue?: string
  gaTrackingLabel?: string
}) {
  const pathname = usePathname()
  const currentLocaleInServer = useLocale()
  const searchParams = useSearchParams()
  const i18nRouter = useRouter()
  const [isPending, startTransition] = useTransition()
  const { start, stop } = useProgress()

  const getBasePath = () => {
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length > 0 && LANGUAGES.some((l) => l.code === segments[0])) {
      const rest = segments.slice(1).join('/')
      return rest ? `/${rest}` : '/'
    }
    return pathname
  }

  const basePath = getBasePath()

  const handleLanguageChange = (lang: string) => {
    if (lang === currentLocaleInServer) return

    // Đánh dấu vừa đổi ngôn ngữ: soft navigation không hydrate lại từ SSR nên
    // CollectionEmptyProductList không tự nhận ra đây là "lần xem đầu" của
    // collection theo ngôn ngữ mới — cần cờ này để nó vẫn thử auto-reload 1
    // lần nếu BE cache cho locale mới chưa kịp warm (xem markLocaleSwitch).
    try {
      sessionStorage.setItem('pending-locale-switch', '1')
    } catch {
      // sessionStorage không khả dụng — bỏ qua, không ảnh hưởng luồng chính
    }

    startTransition(() => {
      setUserLocale(lang)
      start()
      i18nRouter.replace(
        {
          pathname: basePath,
          query: Object.fromEntries(searchParams),
        },
        { locale: lang }
      )

      // Safety net for the manual `start()` above. `@bprogress/next` only
      // stops the bar when it observes a `usePathname()` / `useSearchParams()`
      // change. If the RSC transition triggered by `router.replace` hangs
      // (slow backend or middleware), the bar would otherwise animate forever.
      // We force a `stop()` after 8s as an upper bound. If the transition
      // completes normally before then, the built-in pathname-change handler
      // has already stopped the bar and this call is a no-op.
      setTimeout(() => stop(), 8000)
    })
  }

  const languageOptions = LANGUAGES.filter(
    (lang) => lang.code !== currentLocaleInServer
  ).map((lang) => ({
    value: lang.code,
    label: (
      <Link
        href={basePath}
        locale={lang.code}
        aria-label={`Chuyển sang ${lang.label}`}
        className='flex items-center gap-1'
        ga-tracking-value={gaTrackingValue + '__' + lang.code}
        ga-tracking-label={gaTrackingLabel + '__' + lang.label}
        onClick={(e) => {
          e.preventDefault()
        }}
      >
        <LanguageSwitcherItem
          label={lang.label}
          value={lang.code}
          className='text-neutral-700'
          isLoading={isPending}
        />
        <span className='text-neutral-700'>
          ({lang.code === 'vi' ? 'VN' : 'US'})
        </span>
      </Link>
    ),
  }))

  return (
    <Select
      options={languageOptions}
      value={currentLocaleInServer}
      onValueChange={handleLanguageChange}
      disabled={isPending}
      renderValue={() => (
        <LanguageSwitcherItem
          label={currentLocaleInServer === 'vi' ? 'VN' : 'US'}
          value={currentLocaleInServer}
          isLoading={isPending}
        />
      )}
      className={{
        trigger:
          'h-fit border-none bg-light/70 px-3 py-1.5 text-neutral-700 hover:bg-transparent lg:!bg-transparent lg:p-0 lg:px-2 lg:text-neutral-200 [&_.icon-down]:lg:text-neutral-200',
        content: 'z-[105] bg-neutral-100',
      }}
    />
  )
}
